import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService, User, AuthState } from '@/lib/auth';

interface AuthContextType extends AuthState {
  login: (token?: string) => Promise<User>;
  logout: () => Promise<void>;
  getDiscordAuthUrl: () => Promise<string>;
  signInWithDiscord: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    let isMounted = true;
    
    // Restore session on mount (only once)
    authService.restoreSession().finally(() => {
      if (isMounted) {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    });

    // Subscribe to auth changes
    const unsubscribe = authService.subscribe((newState) => {
      if (isMounted) {
        setState(newState);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const login = async (token: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const user = await authService.login(token);
      return user;
    } catch (error) {
      // Error is already handled and logged in authService
      // Re-throw to let AuthCallback handle the UI
      throw error;
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const logout = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await authService.logout();
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const refresh = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await authService.restoreSession();
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    getDiscordAuthUrl: authService.getDiscordAuthUrl.bind(authService),
    signInWithDiscord: authService.signInWithDiscord.bind(authService),
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
